import React, { ReactElement } from 'react';
import { Space, Table, TableColumnsType, Tooltip } from 'antd';
import moment from 'moment';
import { UserAddOutlined, ContainerOutlined, WarningFilled } from '@ant-design/icons';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import Tag from 'components/new-ui/base/tag/Tag';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import { ManifestStatuses, ManifestStatus as ManifestStatusType } from 'views/manifests/types/manifest-statuses';
import ManifestStatus from '../manifest-status/ManifestStatus';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import { GetManifestsSortParams, Manifest } from 'store/manifests/types';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import Button from 'components/new-ui/base/button/Button';
import { Team } from 'store/teams/types';
import { TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { SortDirection, toAntOrder } from 'utils/tableHelpers';

type Props = {
    manifests?: Manifest[];
    loading?: boolean;
    onAssignManifest?: (manifest: Manifest) => void;
    onArchiveManifest?: (manifest: Manifest) => void;
    onUnarchiveManifest?: (manifest: Manifest) => void;
    onRowClick?: (record: any) => void;
    onChange?: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => void;
    paginator?: TablePaginationConfig;
    currentSortParam?: GetManifestsSortParams;
    currentSortDirection?: SortDirection;
};

const ManifestsTable: React.FC<Props> = ({
    manifests,
    loading,
    onRowClick,
    onAssignManifest,
    onArchiveManifest,
    onUnarchiveManifest,
    onChange,
    paginator,
    currentSortDirection,
    currentSortParam,
}: Props) => {
    const { userInfo } = useSession();

    const columns: TableColumnsType<Manifest> = [
        {
            key: 'notifications',
            width: 1,
            render: (_, record) => {
                const hasTransportDetailsDiscrepancies = record?.transportDetailsMismatch;

                const notificationIcons: ReactElement[] = [];

                if (hasTransportDetailsDiscrepancies) {
                    notificationIcons.push(
                        <Tooltip title="Transport details mismatch">
                            <WarningFilled style={{ color: 'var(--warning-normal)' }} />
                        </Tooltip>
                    );
                }

                return <Space size={8}>{notificationIcons}</Space>;
            },
        },

        {
            title: 'Manifest ID',
            dataIndex: 'referenceId',
            key: GetManifestsSortParams.REFERENCE_ID,
            sorter: true,
            sortOrder:
                currentSortParam === GetManifestsSortParams.REFERENCE_ID ? toAntOrder(currentSortDirection) : null,
            render: (referenceId: string) => {
                const dataTestId = 'manifiests-table-reference-row';
                return (
                    <TooltipWithCopy copyText={referenceId} dataTestId={dataTestId}>
                        {referenceId}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'PBN',
            dataIndex: 'pbn',
            key: GetManifestsSortParams.PBN,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.PBN ? toAntOrder(currentSortDirection) : null,
            render: (pbn?: string) => {
                const dataTestId = 'manifests-table-pbn-row';
                if (!pbn) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }
                return (
                    <TooltipWithCopy copyText={pbn} dataTestId={dataTestId}>
                        {pbn}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'GMR',
            dataIndex: 'gmr',
            key: GetManifestsSortParams.GMR,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.GMR ? toAntOrder(currentSortDirection) : null,
            render: (gmr?: string) => {
                const dataTestId = 'manifests-table-gmr-row';
                if (!gmr) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }
                return (
                    <TooltipWithCopy copyText={gmr} dataTestId={dataTestId}>
                        {gmr}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Jobs',
            dataIndex: 'jobs',
            key: 'NUM_JOBS',
            render: (_, record: Manifest) => {
                return record?.jobs?.length ?? 0;
            },
        },

        {
            title: 'Team',
            dataIndex: 'team',
            key: GetManifestsSortParams.TEAM_ID,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.TEAM_ID ? toAntOrder(currentSortDirection) : null,
            render: (team: Team) => {
                if (!team.name) {
                    return '-';
                }

                if (team.name === 'Harbour IE') {
                    return <Tag label={team.name} status="success"></Tag>;
                }

                return <Tag label={team.name} status="default"></Tag>;
            },
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: GetManifestsSortParams.CREATED_BY,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.CREATED_BY ? toAntOrder(currentSortDirection) : null,
            render: (_, record: Manifest) => {
                const date = moment(record.createdAt);
                const formattedDate = date.format('DD/MM/YY');
                const formattedTime = date.format('HH:mm A');
                const creatorName = record?.creator?.fullName ?? record?.creator?.email ?? '-';
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <span>{creatorName}</span>
                        <span>
                            {formattedDate}, {formattedTime}
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            key: GetManifestsSortParams.OWNER_ID,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.OWNER_ID ? toAntOrder(currentSortDirection) : null,
            render: (_, record: Manifest) => {
                if (!record?.owner?.id) {
                    if (hasPermissions(userInfo, 'manifests', 'UPDATE')) {
                        return (
                            <Button
                                type="assign"
                                title="Assign Manifest"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAssignManifest?.(record);
                                }}
                            />
                        );
                    }

                    return <EmptyCell />;
                }

                const firstName = record?.owner?.fullName?.split(' ')[0];

                return (
                    <Space size={8}>
                        <UserAvatar
                            size="small"
                            userName={record.owner.fullName}
                            src={record.owner.profilePictureLink}
                        />
                        {firstName || record.owner.email}
                    </Space>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: GetManifestsSortParams.STATUS,
            sorter: true,
            sortOrder: currentSortParam === GetManifestsSortParams.STATUS ? toAntOrder(currentSortDirection) : null,
            render: (status: ManifestStatusType) => {
                return <ManifestStatus status={status} />;
            },
        },
        {
            title: 'Actions',
            key: 'ACTIONS',
            render: (record: Manifest) => {
                const canArchiveManifests = hasPermissions(userInfo, 'manifests', 'ARCHIVE');
                const isActiveManifest = record.status === ManifestStatuses.ACTIVE;
                const disabled = !canArchiveManifests || isActiveManifest;

                return (
                    <Space size={8}>
                        <IconButton
                            icon={<UserAddOutlined />}
                            tooltipText="Assign Manifest"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAssignManifest?.(record);
                            }}
                        />

                        {record.archived ? (
                            <IconButton
                                disabled={disabled}
                                icon={<ContainerOutlined />}
                                tooltipText="Unarchive Manifest"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUnarchiveManifest?.(record);
                                }}
                            />
                        ) : (
                            <IconButton
                                disabled={disabled}
                                icon={<ContainerOutlined />}
                                tooltipText="Archive Manifest"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onArchiveManifest?.(record);
                                }}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const hasAnyNotification = manifests?.some((m) => m.transportDetailsMismatch) ?? false;

    const filteredColumnsByAccess = !hasPermissions(userInfo, 'manifests', 'UPDATE')
        ? columns.filter((col) => col.key !== 'ACTIONS')
        : columns;

    const visibleColumns = hasAnyNotification
        ? filteredColumnsByAccess
        : filteredColumnsByAccess.filter((col) => col.key !== 'notifications');

    return (
        <StyledTableWrapper>
            <Table<Manifest>
                loading={loading}
                rowKey="id"
                onRow={(record) => ({
                    onClick: () => onRowClick?.(record),
                    style: { cursor: 'pointer' },
                })}
                dataSource={manifests}
                columns={visibleColumns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                onChange={onChange}
                pagination={paginator}
                scroll={{ x: 'max-content' }}
            />
        </StyledTableWrapper>
    );
};

export default ManifestsTable;
